import { useState, useCallback } from 'react'
import { useSearch, useNavigate } from '@tanstack/react-router'
import { LuX, LuSearch, LuRotateCcw } from 'react-icons/lu'
import CustomHeroSection from '#/components/common/CustomHeroSection'
import BlogsFilter from '#/features/blogs/components/BlogsFilter'
import BlogsFilterDrawer from '#/features/blogs/components/BlogsFilterDrawer'
import BlogsList from '#/features/blogs/components/BlogsList'
import { useTags } from '#/features/courses/hooks/useTags'
import blogHeroImage from '#/assets/blog/blog-2.png'
import type { BlogsSearch } from '#/routes/_home/blog/index'
import PageTransition from '#/components/common/PageTransition'
import { ScrollReveal, RevealItem } from '#/components/common/ScrollReveal'

export default function AllBlogsPage() {
  const search = (useSearch({ strict: false }) || {}) as BlogsSearch
  const navigate = useNavigate()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const currentPage = search.page || 1
  const searchQuery = search.search || ''
  const activeTags = search.tags ? search.tags.split(',').filter(Boolean) : []

  const { data: tagsRes } = useTags()
  const tags = Array.isArray(tagsRes) ? tagsRes : []

  const activeFilterCount = activeTags.length + (searchQuery ? 1 : 0)

  const handleToggleTag = useCallback(
    (tagSlug: string) => {
      const current = search.tags ? search.tags.split(',').filter(Boolean) : []
      const updated = current.includes(tagSlug)
        ? current.filter((item) => item !== tagSlug)
        : [...current, tagSlug]
      navigate({
        to: '/blog',
        search: {
          ...search,
          tags: updated.length > 0 ? updated.join(',') : undefined,
          page: 1,
        },
      })
    },
    [navigate, search],
  )

  const handleClearTags = useCallback(() => {
    navigate({
      to: '/blog',
      search: {
        ...search,
        tags: undefined,
        page: 1,
      },
    })
  }, [navigate, search])

  const handleSearchChange = (query: string) => {
    navigate({
      to: '/blog',
      search: {
        ...search,
        search: query.trim() ? query : undefined,
        page: 1,
      },
    })
  }

  const handleClearAll = () => {
    navigate({
      to: '/blog',
      search: {
        page: 1,
      },
    })
  }

  const handlePageChange = (pageZeroIndexed: number) => {
    const targetPage = pageZeroIndexed + 1
    navigate({
      to: '/blog',
      search: {
        ...search,
        page: targetPage,
      },
    })
  }

  return (
    <PageTransition>
      <main className="flex relative flex-col bg-secondary-50/40 min-h-screen">
        <CustomHeroSection
          title="All Blogs"
          breadcrumbLabel="Blog"
          backgroundImage={blogHeroImage}
          gradientBackground="secondary-gradient"
        />

        <section className="w-full py-10 sm:py-14">
          <div className="container mx-auto px-4">
            {/* ── Responsive Top Filter Bar (Visible on Mobile / Tablet < xl) ── */}
            <div className="mb-6 flex flex-col gap-3 xl:hidden">
              <div className="flex items-center gap-2.5">
                {/* Search input */}
                <div className="relative flex-1">
                  <label className="flex items-center gap-2.5 rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 text-sm text-neutral-600 shadow-xs focus-within:border-secondary-500 focus-within:ring-2 focus-within:ring-secondary-500/20">
                    <LuSearch className="h-4 w-4 text-neutral-400 shrink-0" />
                    <input
                      className="w-full bg-transparent text-sm text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
                      placeholder="Search articles..."
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => handleSearchChange('')}
                        className="text-neutral-400 hover:text-neutral-600 p-0.5"
                      >
                        <LuX className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </label>
                </div>

                {/* Drawer trigger button */}
                <BlogsFilterDrawer
                  open={drawerOpen}
                  onOpenChange={setDrawerOpen}
                  tags={tags}
                  activeTags={activeTags}
                  onToggleTag={handleToggleTag}
                  onClearTags={handleClearTags}
                  searchQuery={searchQuery}
                  onSearchChange={handleSearchChange}
                  activeFilterCount={activeFilterCount}
                  onClearAll={handleClearAll}
                />
              </div>

              {/* Active filter badges row on mobile */}
              {activeFilterCount > 0 && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <span className="text-xs font-medium text-neutral-500">
                    Active:
                  </span>

                  {activeTags.map((slug) => {
                    const tagObj = tags.find((t) => t.slug === slug)
                    return (
                      <span
                        key={slug}
                        className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100/80 px-3 py-1 text-xs font-semibold text-secondary-800 border border-secondary-300/60"
                      >
                        Tag: {tagObj?.name || slug}
                        <button
                          type="button"
                          onClick={() => handleToggleTag(slug)}
                          className="rounded-full p-0.5 hover:bg-secondary-200 text-secondary-700"
                        >
                          <LuX className="h-3 w-3" />
                        </button>
                      </span>
                    )
                  })}

                  {searchQuery && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-200/80 px-3 py-1 text-xs font-semibold text-neutral-800 border border-neutral-300">
                      "{searchQuery}"
                      <button
                        type="button"
                        onClick={() => handleSearchChange('')}
                        className="rounded-full p-0.5 hover:bg-neutral-300 text-neutral-700"
                      >
                        <LuX className="h-3 w-3" />
                      </button>
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="text-xs font-semibold text-neutral-400 hover:text-red-600 underline underline-offset-2 ml-1"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* ── Main Grid (Sidebar on Desktop xl, List on all) ── */}
            <ScrollReveal
              stagger={0.1}
              className="grid grid-cols-1 gap-8 xl:grid-cols-[320px_minmax(0,1fr)]"
            >
              {/* Desktop Sidebar */}
              <RevealItem direction="down" offset={16} scale={0.99}>
                <aside className="hidden xl:block">
                  <div className="sticky top-28">
                    <BlogsFilter
                      tags={tags}
                      activeTags={activeTags}
                      onToggleTag={handleToggleTag}
                      onClearTags={handleClearTags}
                      searchQuery={searchQuery}
                      onSearchChange={handleSearchChange}
                      variant="sidebar"
                    />
                  </div>
                </aside>
              </RevealItem>

              {/* Blogs List Container */}
              <RevealItem direction="down" offset={16} scale={0.99}>
                <div className="flex flex-col gap-6">
                  {/* Desktop Active Filters Pill Bar */}
                  {activeFilterCount > 0 && (
                    <div className="hidden xl:flex items-center justify-between rounded-2xl border border-neutral-200/80 bg-white px-5 py-3 shadow-xs">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mr-1">
                          Active Filters:
                        </span>
                        {activeTags.map((slug) => {
                          const tagObj = tags.find((t) => t.slug === slug)
                          return (
                            <span
                              key={slug}
                              className="inline-flex items-center gap-1.5 rounded-full bg-secondary-50 px-3 py-1 text-xs font-semibold text-secondary-800 border border-secondary-200"
                            >
                              Tag: {tagObj?.name || slug}
                              <button
                                type="button"
                                onClick={() => handleToggleTag(slug)}
                                className="rounded-full p-0.5 hover:bg-secondary-200 text-secondary-700"
                              >
                                <LuX className="h-3 w-3" />
                              </button>
                            </span>
                          )
                        })}
                        {searchQuery && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-800 border border-neutral-200">
                            "{searchQuery}"
                            <button
                              type="button"
                              onClick={() => handleSearchChange('')}
                              className="rounded-full p-0.5 hover:bg-neutral-200 text-neutral-700"
                            >
                              <LuX className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleClearAll}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        <LuRotateCcw className="h-3 w-3" />
                        Clear all
                      </button>
                    </div>
                  )}

                  <BlogsList
                    page={currentPage}
                    activeTags={activeTags}
                    searchQuery={searchQuery}
                    onPageChange={handlePageChange}
                  />
                </div>
              </RevealItem>
            </ScrollReveal>
          </div>
        </section>
      </main>
    </PageTransition>
  )
}
